import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Button,
  ChoiceList,
  InlineStack,
  LegacyFilters,
  Modal,
  Pagination,
  ResourceItem,
  ResourceList,
  SkeletonBodyText,
  Spinner,
  Text,
  Thumbnail,
  BlockStack,
} from '@shopify/polaris';
import {useQuery} from '@shopify/react-graphql';
import {useToggle} from '@shopify/react-hooks';
import {nodesFromEdges} from '@shopify/admin-graphql-api-utilities';
import {FileSortKeys} from 'types/graphql/default-types';
import {ImageIcon} from '@shopify/polaris-icons';
import {useI18n} from '@shopify/react-i18n';

import FilesQuery, {FilesQueryData} from './graphql/FilesQuery.graphql';
import styles from './FilePicker.scss';

export interface FilePickerFile {
  id: string;
  alt: string;
  displayName: string;
  previewUrl: string;
}

export enum SupportedFileType {
  All = 'All',
  Image = 'Image',
  Video = 'Video',
}
export interface FilePickerProps {
  resourceId: string;
  onChange: (selectedFile?: FilePickerFile) => void;
  supportedFileTypes?: (keyof typeof SupportedFileType)[];
  readOnly: boolean;
  originalFile?: FilePickerFile | null;
}

const DEFAULT_PAGINATION_LIMIT = 10;
const scope = ['FilePicker'];

type SupportedFileTypeFromQuery =
  | FilesQueryData.FilesEdgesNodeMediaImage
  | FilesQueryData.FilesEdgesNodeVideo
  | FilesQueryData.FilesEdgesNodeGenericFile;

export function FilePicker({
  resourceId,
  onChange,
  supportedFileTypes = [SupportedFileType.All],
  readOnly,
  originalFile,
}: FilePickerProps) {
  const [i18n] = useI18n();
  const [currentSelections, setCurrentSelections] = useState<string[]>([]);
  const [currentFile, setCurrentFile] = useState<FilePickerFile | undefined>(
    undefined,
  );
  const [userQuery, setUserQuery] = useState('');
  const [sortBy, setSortBy] = useState('CREATED_AT-DESC');
  const [pagination, setPagination] = useState<{
    direction: 'next' | 'previous';
    cursor?: string;
  }>({
    direction: 'next',
    cursor: undefined,
  });
  const {
    value: modalVisible,
    setTrue: showModal,
    setFalse: hideModal,
  } = useToggle(false);

  const imageOnly = useMemo(
    () =>
      supportedFileTypes.length === 1 &&
      supportedFileTypes[0] === SupportedFileType.Image,
    [supportedFileTypes],
  );
  const [sortValue, sortOrder] = sortBy.split('-');
  const reverse = sortOrder === 'DESC';
  const sortKey = sortValue
    ? (sortValue as FileSortKeys)
    : FileSortKeys.CreatedAt;

  const query = useMemo(() => {
    const parts = [`status:ready`];

    if (userQuery) {
      parts.push(`filename:${userQuery}`);
    }

    const mediaTypesGroup = (() => {
      if (supportedFileTypes.includes(SupportedFileType.All)) {
        return '';
      }

      const types = [];
      if (supportedFileTypes.includes(SupportedFileType.Image)) {
        types.push('IMAGE');
      }

      if (supportedFileTypes.includes(SupportedFileType.Video)) {
        types.push('VIDEO');
      }

      return `(media_type:${types.join(' OR media_type:')})`;
    })();

    if (mediaTypesGroup) {
      parts.push(mediaTypesGroup);
    }

    return parts.join(' AND ');
  }, [userQuery, supportedFileTypes]);

  const {data, loading} = useQuery(FilesQuery, {
    variables: {
      first:
        pagination.direction === 'next' ? DEFAULT_PAGINATION_LIMIT : undefined,
      last:
        pagination.direction === 'previous'
          ? DEFAULT_PAGINATION_LIMIT
          : undefined,
      query,
      reverse,
      sortKey,
      after: pagination.direction === 'next' ? pagination.cursor : undefined,
      before:
        pagination.direction === 'previous' ? pagination.cursor : undefined,
    },
    skip: !modalVisible,
  });

  const {hasNextPage, hasPreviousPage, startCursor, endCursor} = useMemo(
    () =>
      data?.files?.pageInfo || {
        startCursor: undefined,
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    [data],
  );

  const files = useMemo(
    () =>
      nodesFromEdges(data?.files.edges || []).filter(
        (file): file is SupportedFileTypeFromQuery => 'id' in file,
      ),
    [data?.files.edges],
  );

  useEffect(() => {
    // Unset selection on any data.files change
    // Cause rendering issues with the ResourceList if we don't
    setCurrentSelections([]);

    /**
     * Reset state when resourceId is set back to the original media id
     */
    if (resourceId === originalFile?.id) {
      setCurrentFile(undefined);
    }

    /**
     * Set the current selection when the resourceId is part of the results in the current page
     */
    if (resourceId && files.find((file) => file.id === resourceId)) {
      setCurrentSelections([resourceId]);
    }
  }, [resourceId, originalFile, files]);

  const currentFileBlock = useMemo(() => {
    if (!resourceId) {
      return null;
    }

    if (originalFile === undefined) {
      return <Spinner size="small" />;
    }

    const file = currentFile ?? originalFile;
    let thumbnail = <Thumbnail size="small" source={ImageIcon} alt="" />;

    if (!file) {
      return thumbnail;
    }

    const {alt, displayName} = file;
    if (file.previewUrl) {
      thumbnail = (
        <Thumbnail
          key={`preview-${file.id}`}
          size="small"
          source={file.previewUrl}
          alt={alt || ''}
        />
      );
    }

    return (
      <InlineStack blockAlign="center" wrap={false} gap="400">
        {thumbnail}
        <BlockStack gap="200">
          {alt && (
            <Text as="p" fontWeight="bold">
              {alt}
            </Text>
          )}
          {displayName && (
            <Text as="p" tone="subdued">
              {displayName}
            </Text>
          )}
        </BlockStack>
      </InlineStack>
    );
  }, [currentFile, originalFile, resourceId]);

  const currentFileStatus = useMemo(
    () => (
      <div className={styles.CurrentFileContainer}>
        <InlineStack
          align="space-between"
          blockAlign="center"
          wrap={false}
          gap="400"
        >
          <>{currentFileBlock}</>
          {!readOnly && (
            <Button size="slim" onClick={showModal}>
              {i18n.translate('changeButtonLabel', {scope})}
            </Button>
          )}
        </InlineStack>
      </div>
    ),
    [currentFileBlock, i18n, readOnly, showModal],
  );

  const handleSelectionChange = useCallback(
    (fileId?: string) => {
      const matchingFile = fileId
        ? files.find((file) => file.id === fileId)
        : undefined;
      const formattedFile: FilePickerFile = {
        id: matchingFile?.id || '',
        alt: matchingFile?.alt || '',
        displayName: matchingFile?.displayName || '',
        previewUrl: matchingFile?.preview?.image?.url || '',
      };
      if (matchingFile) {
        setCurrentFile(formattedFile);
        onChange(formattedFile);
      } else {
        setCurrentFile(undefined);
        onChange(undefined);
      }
    },
    [files, onChange],
  );

  const previousPage = useCallback(() => {
    if (startCursor) {
      setCurrentSelections([]);
      setPagination({
        direction: 'previous',
        cursor: startCursor,
      });
    }
  }, [startCursor]);

  const nextPage = useCallback(() => {
    if (endCursor) {
      setCurrentSelections([]);
      setPagination({
        direction: 'next',
        cursor: endCursor,
      });
    }
  }, [endCursor]);

  const resetPagination = useCallback(() => {
    setPagination({
      direction: 'next',
      cursor: undefined,
    });
  }, []);

  const resetQuery = useCallback(() => {
    setUserQuery('');
  }, []);

  const resetSort = useCallback(() => {
    setSortBy('CREATED_AT-DESC');
  }, []);

  const resetAll = useCallback(() => {
    resetPagination();
    resetQuery();
    resetSort();
  }, [resetPagination, resetQuery, resetSort]);

  const hideModalAndResetStates = useCallback(() => {
    hideModal();
    resetAll();
  }, [hideModal, resetAll]);

  const renderItem = useCallback(
    (props: {id: string; file?: SupportedFileTypeFromQuery}) => {
      if (!props.file || loading) {
        return (
          <ResourceItem
            id={props.id}
            media={<Thumbnail size="small" source={ImageIcon} alt="" />}
            onClick={() => {}}
          >
            <SkeletonBodyText lines={2} />
          </ResourceItem>
        );
      } else {
        const {id, alt, displayName, createdAt} = props.file;
        let media = <Thumbnail size="small" source={ImageIcon} alt="" />;

        if ('preview' in props.file && props.file.preview?.image?.url) {
          media = (
            <Thumbnail
              key={props.file.preview.image.id}
              size="small"
              source={props.file.preview.image.url}
              alt={alt || ''}
            />
          );
        }

        return (
          <ResourceItem
            id={id}
            media={media}
            onClick={() => {
              handleSelectionChange(id);
            }}
          >
            <h3>
              <Text as="span" fontWeight="bold">
                {displayName}
              </Text>
            </h3>
            {alt && <div>{alt}</div>}
            <>{createdAt}</>
          </ResourceItem>
        );
      }
    },
    [handleSelectionChange, loading],
  );

  const filters = useMemo(
    () => [
      {
        key: 'sort',
        label: i18n.translate('sortLabel', {
          scope,
        }),
        shortcut: true,
        hideClearButton: true,
        filter: (
          <ChoiceList
            title={i18n.translate('sortByChoicesList.sortBy', {
              scope,
            })}
            titleHidden
            choices={[
              {
                label: i18n.translate('CREATED_AT.DESC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'CREATED_AT-DESC',
              },
              {
                label: i18n.translate('CREATED_AT.ASC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'CREATED_AT-ASC',
              },
              {
                label: i18n.translate('FILENAME.ASC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'FILENAME-ASC',
              },
              {
                label: i18n.translate('FILENAME.DESC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'FILENAME-DESC',
              },
              {
                label: i18n.translate('ORIGINAL_UPLOAD_SIZE.ASC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'ORIGINAL_UPLOAD_SIZE-ASC',
              },
              {
                label: i18n.translate('ORIGINAL_UPLOAD_SIZE.DESC', {
                  scope: [...scope, 'sortByChoicesList'],
                }),
                value: 'ORIGINAL_UPLOAD_SIZE-DESC',
              },
            ]}
            selected={sortBy ? [sortBy] : []}
            onChange={(selected) => {
              resetPagination();
              resetQuery();
              setSortBy(selected[0]);
            }}
          />
        ),
      },
    ],
    [i18n, resetPagination, resetQuery, sortBy],
  );

  const filterControl = useMemo(
    () => (
      <LegacyFilters
        queryValue={userQuery}
        onQueryChange={(queryValue) => {
          resetPagination();
          setUserQuery(queryValue);
        }}
        onQueryClear={() => {
          resetPagination();
          resetQuery();
        }}
        filters={filters}
        onClearAll={resetAll}
      />
    ),
    [filters, userQuery, resetAll, resetPagination, resetQuery],
  );

  const paginator = useMemo(
    () => (
      <Pagination
        hasPrevious={hasPreviousPage}
        onPrevious={previousPage}
        hasNext={hasNextPage}
        onNext={nextPage}
      />
    ),
    [hasNextPage, hasPreviousPage, nextPage, previousPage],
  );

  const items = useMemo<
    {
      id: string;
      file?: SupportedFileTypeFromQuery;
    }[]
  >(
    () =>
      loading
        ? Array.from({length: DEFAULT_PAGINATION_LIMIT}).map((_, i) => ({
            id: `skeleton-${i}`,
          }))
        : files.map((file) => ({
            id: file.id,
            file,
          })),
    [loading, files],
  );

  const selectLabel = useMemo(() => {
    if (imageOnly) {
      return 'selectImageLabel';
    }

    if (
      supportedFileTypes.length > 1 &&
      supportedFileTypes.includes(SupportedFileType.Image) &&
      supportedFileTypes.includes(SupportedFileType.Video)
    ) {
      return 'selectImageOrVideoLabel';
    }

    if (supportedFileTypes.includes(SupportedFileType.Video)) {
      return 'selectVideoLabel';
    }

    return 'selectFileLabel';
  }, [imageOnly, supportedFileTypes]);

  const modal = useMemo(
    () =>
      readOnly ? null : (
        <Modal
          activator={<></>}
          open={modalVisible}
          onClose={hideModalAndResetStates}
          title={i18n.translate(selectLabel, {
            scope,
          })}
          primaryAction={{
            content: i18n.translate('Common.close'),
            onAction: hideModalAndResetStates,
          }}
          secondaryActions={[
            {
              content: i18n.translate('Common.clear'),
              destructive: true,
              onAction: () => {
                onChange(undefined);
              },
              disabled: !resourceId,
            },
          ]}
        >
          <Modal.Section flush>
            <ResourceList
              loading={loading}
              resourceName={{
                singular: i18n.translate(
                  imageOnly ? 'imageResourceName.one' : 'fileResourceName.one',
                  {
                    scope,
                  },
                ),
                plural: i18n.translate(
                  imageOnly
                    ? 'imageResourceName.other'
                    : 'fileResourceName.other',
                  {
                    scope,
                  },
                ),
              }}
              filterControl={filterControl}
              selectedItems={currentSelections}
              onSelectionChange={(selectedItems) => {
                handleSelectionChange(
                  selectedItems.length && selectedItems[1]
                    ? selectedItems[1]
                    : undefined,
                );
              }}
              items={items}
              renderItem={renderItem}
            />
          </Modal.Section>
          <Modal.Section>{paginator}</Modal.Section>
        </Modal>
      ),
    [
      readOnly,
      modalVisible,
      hideModalAndResetStates,
      i18n,
      selectLabel,
      loading,
      imageOnly,
      filterControl,
      currentSelections,
      items,
      renderItem,
      paginator,
      handleSelectionChange,
      onChange,
      resourceId,
    ],
  );

  return (
    <>
      {currentFileStatus}
      {modal}
    </>
  );
}
